import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import AdminNavBar from "../components/AdminNavBar";
import {
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  AdminLoadingState,
} from "../components/AdminPageShell";
import { useAdminStaff } from "../store/admin";
import StaffPagination from "./components/staff/StaffPagination";
import { formatDate, staffName } from "./components/staff/staffUtils";

// ===== STAFF ACTIVITY PAGE — GET /api/admin/staff/:id/activity?page&limit =====
// Shows only the activity log for a staff member. Profile lives on a separate
// page at /admin/staff-management/:staffId.
const StaffActivityPage = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();

  const {
    staffDetail,
    staffDetailLoading,
    staffDetailError,
    fetchStaffMember,
    staffActivity,
    staffActivityLoading,
    staffActivityError,
    staffActivityTotal,
    staffActivityPages,
    fetchStaffActivity,
  } = useAdminStaff();

  const [activityPage, setActivityPage] = useState(1);

  // GET /api/admin/staff/:id — for the header name
  useEffect(() => {
    if (staffId) {
      fetchStaffMember(staffId);
    }
  }, [staffId, fetchStaffMember]);

  // GET /api/admin/staff/:id/activity?page&limit
  useEffect(() => {
    if (staffId) {
      fetchStaffActivity(staffId, { page: activityPage, limit: 10 });
    }
  }, [staffId, activityPage, fetchStaffActivity]);

  const goBack = () => navigate("/admin/staff-management");

  const title = staffDetail ? staffName(staffDetail) : "Staff";

  return (
    <div>
      <AdminNavBar
        title="Staff Activity"
        subTitle={title}
        context="Audit log of actions performed on this staff member's account."
      />
      <AdminPageBody>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Staff Management
          </button>
          <Link
            to={`/admin/staff-management/${staffId}`}
            className="inline-flex items-center rounded-lg border border-[#16213A] bg-[#16213A] px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm transition hover:bg-[#203052] no-underline"
          >
            View Profile
          </Link>
        </div>

        <div className="mb-3">
          <h4 className="text-lg font-bold text-slate-900">{title}</h4>
          <span className="text-[13px] text-slate-500">{staffDetail?.email || ""}</span>
        </div>

        {staffDetailLoading && !staffDetail ? (
          <div className="mb-4">
            <AdminLoadingState />
          </div>
        ) : staffDetailError ? (
          <div className="mb-4 p-4 text-sm text-red-600">{staffDetailError}</div>
        ) : null}

        {/* ACTIVITY LOG */}
        <AdminSectionHeader
          title="Activity Log"
          description="Latest actions recorded for this staff member."
          count={`${staffActivityTotal ?? staffActivity.length} entries`}
        />

        <AdminTablePanel className="mt-4">
          {staffActivityLoading ? (
            <AdminLoadingState />
          ) : staffActivityError ? (
            <div className="p-4 text-sm text-red-600">{staffActivityError}</div>
          ) : staffActivity.length === 0 ? (
            <div className="flex min-h-[220px] items-center justify-center rounded-lg text-slate-500">
              No activity recorded for this staff member.
            </div>
          ) : (
            <table className="admin-table-responsive w-full border-collapse text-[13px]">
              <thead>
                <tr>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Actor</th>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Action</th>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Target</th>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Date</th>
                </tr>
              </thead>
              <tbody>
                {staffActivity.map((entry, i) => (
                  <tr key={entry.id ?? i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td data-label="Actor" className="px-3 py-2.5">
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-slate-700">
                        {entry.actorType || entry.actor || "--"}
                      </span>
                    </td>
                    <td data-label="Action" className="px-3 py-2.5 font-medium text-slate-800">
                      {entry.action || entry.type || entry.title || "Activity"}
                    </td>
                    <td data-label="Target" className="px-3 py-2.5">
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-blue-700">
                        {entry.targetType || entry.target || "--"}
                      </span>
                    </td>
                    <td data-label="Date" className="px-3 py-2.5 text-slate-500">
                      {formatDate(entry.createdAt || entry.created_at || entry.timestamp)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </AdminTablePanel>

        {!staffActivityLoading && !staffActivityError && staffActivity.length !== 0 && (
          <div className="mt-4">
            <StaffPagination
              page={activityPage}
              lastPage={staffActivityPages}
              onPageChange={setActivityPage}
            />
          </div>
        )}
      </AdminPageBody>
    </div>
  );
};

export default StaffActivityPage;